type LayoutMetricSlice = { roofCount?: number | null };

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

/** Count roofs that have at least one module after layout simulation. */
export function countRoofsWithModulesFromRoofInfo(
    roofInfoArr: Array<{ roofFaceId?: string; modules?: number | string }> | undefined | null,
    candidateRoofIds?: string[],
): number {
    if (!roofInfoArr?.length) return 0;
    const idSet = candidateRoofIds?.length
        ? new Set(candidateRoofIds.filter(Boolean))
        : null;
    return roofInfoArr.filter((r) => {
        if (idSet && r.roofFaceId && !idSet.has(r.roofFaceId)) return false;
        const moduleCount =
            typeof r.modules === 'number' ? r.modules : parseInt(String(r.modules ?? '0'), 10) || 0;
        return moduleCount > 0;
    }).length;
}

/** Roof count for AI catalog rows — roofs with modules, not roofs in the candidate set. */
export function resolveLayoutRoofCount(args: {
    layoutKey: string;
    layoutMetricsByKey: Record<string, LayoutMetricSlice | undefined> | undefined;
    defaultMaxfillRoofIds: string[] | undefined;
    roofCards: Array<{ roofFaceId?: string; moduleCount?: number }> | undefined;
    roofCombinationsFromAi: Array<{ name?: string; roofFaceIds?: string[] }> | undefined;
    categoryMainLayoutByCategory?: Record<string, { roofCount?: number | null }>;
    layoutRoofSummariesByKey?: Record<string, unknown[] | undefined>;
}): number {
    const {
        layoutKey,
        layoutMetricsByKey,
        defaultMaxfillRoofIds,
        roofCards,
        roofCombinationsFromAi,
        categoryMainLayoutByCategory,
        layoutRoofSummariesByKey,
    } = args;
    const metrics = layoutMetricsByKey?.[layoutKey];
    const roofCountFromMetrics =
        metrics?.roofCount != null && Number.isFinite(metrics.roofCount) ? metrics.roofCount : null;

    if (roofCountFromMetrics != null && roofCountFromMetrics > 0) {
        return roofCountFromMetrics;
    }

    if (layoutKey.startsWith(CATEGORY_MAIN_KEY_PREFIX)) {
        const category = layoutKey.slice(CATEGORY_MAIN_KEY_PREFIX.length);
        const mainRoofCount = categoryMainLayoutByCategory?.[category]?.roofCount;
        if (mainRoofCount != null && Number.isFinite(mainRoofCount) && mainRoofCount > 0) {
            return mainRoofCount;
        }
    }

    const summaries = layoutRoofSummariesByKey?.[layoutKey];
    if (Array.isArray(summaries) && summaries.length > 0) {
        return summaries.length;
    }

    const roofsWithModulesFromCards = (roofCards || []).filter(
        (r) => typeof r.moduleCount === 'number' && r.moduleCount > 0,
    ).length;
    if (roofsWithModulesFromCards > 0 && layoutKey === 'default') {
        return roofsWithModulesFromCards;
    }

    const defaultRoofIds =
        (defaultMaxfillRoofIds?.length ?? 0) > 0
            ? defaultMaxfillRoofIds!
            : ((roofCards || []).map((r) => r.roofFaceId).filter(Boolean) as string[]);

    if (layoutKey === 'default') {
        return defaultRoofIds.length;
    }

    const combo = (roofCombinationsFromAi || []).find((c) => c.name === layoutKey);
    return Array.isArray(combo?.roofFaceIds) ? combo.roofFaceIds.length : 0;
}

type LayoutMetricsWithTreeCount = Record<string, { treeCount?: number | null } | undefined>;

/** Count site trees once — scene group, store, prop, then layout metrics captured during simulation. */
export function countSiteTrees(args: {
    sceneViewer?: { treeGroup?: { children?: unknown[] } } | null;
    treeArr?: Array<{ delStatus?: boolean }>;
    treeCountProp?: number;
    layoutMetricsByKey?: LayoutMetricsWithTreeCount;
}): number {
    const { sceneViewer, treeArr, treeCountProp, layoutMetricsByKey } = args;
    const children = ((sceneViewer as { treeGroup?: { children?: Array<{ delStatus?: boolean }> } })?.treeGroup
        ?.children || []) as Array<{ delStatus?: boolean }>;
    const fromScene =
        Array.isArray(children) && children.length > 0
            ? children.filter((t) => !t?.delStatus).length
            : (treeArr || []).filter((t) => !t?.delStatus).length;

    if (fromScene > 0) return fromScene;
    if (typeof treeCountProp === 'number' && treeCountProp > 0) return treeCountProp;

    if (layoutMetricsByKey) {
        for (const metrics of Object.values(layoutMetricsByKey)) {
            const tc = metrics?.treeCount;
            if (tc != null && Number.isFinite(tc) && tc > 0) return tc;
        }
    }

    return fromScene;
}

/** Trees are site-wide; per-layout metrics may omit treeCount after category selection. */
export function resolveSiteTreeCount(siteTreeCount: number): number {
    return Number.isFinite(siteTreeCount) && siteTreeCount >= 0 ? siteTreeCount : 0;
}

/** Simulated layout production and SF annual usage are both annual kWh (see TotalSystemEfficiency). */
export function isLayoutProductionOverAnnualUsage(
    productionKwh: number | undefined,
    annualUsageKwh: number | null | undefined,
): boolean {
    return (
        annualUsageKwh != null &&
        typeof productionKwh === 'number' &&
        Number.isFinite(productionKwh) &&
        productionKwh > annualUsageKwh
    );
}

/** Tooltip copy when a layout is disqualified because production exceeds site annual usage. */
export function formatLayoutNotSellableReason(annualUsageKwh?: number | null): string {
    if (annualUsageKwh != null && Number.isFinite(annualUsageKwh)) {
        return `Estimated production exceeds annual usage (${Math.round(annualUsageKwh).toLocaleString()} kWh/yr).`;
    }
    return 'Estimated production exceeds annual usage.';
}
